import produce from "immer";
import create from "zustand";
import { SongId } from "../../models/Song";
import { usePlayer } from "./usePlayer";

export type PlaylistState = {
  title: string | undefined;
  id: string | undefined;
  saved: string | undefined;
  songs: SongId[];
  nowPlayingId?: SongId;
  // Optional "load the next page" provider for continuous (YouTube-style)
  // autoplay. When set (e.g. by "Play All" on the feed), advance() calls it once
  // the queue runs out to append the next page of songs and keep playing —
  // instead of pre-queuing the entire feed. null = finite queue (stop / loop).
  loadMore: (() => Promise<SongId[]>) | null;
  nextSong: () => SongId;
  previousSong: () => SongId;
  queueSong: (id: SongId) => void;
  removeSong: (id: SongId) => void;
  setNowPlaying: (id: SongId) => void;
  setLoadMore: (fn: (() => Promise<SongId[]>) | null) => void;
  // Async "what plays next when the current song ends": advances within the
  // queue, else refills from loadMore (autoplay), else loops, else stops.
  advance: () => Promise<SongId>;
};

export const usePlaylist = create<PlaylistState>((set, get) => ({
  title: undefined,
  saved: undefined,
  id: undefined,
  songs: [],
  nowPlayingId: "",
  loadMore: null,
  setNowPlaying: (id) => {
    set({ nowPlayingId: id });
  },
  setLoadMore: (fn) => {
    set({ loadMore: fn });
  },
  queueSong: (id) => {
    const playlistSongs = get().songs;
    const playlistWithNewSong = produce(playlistSongs, (songs) => {
      if (!songs.includes(id)) songs.push(id);
    });
    set({ songs: playlistWithNewSong });
  },
  removeSong: (id) => {
    const playlistSongs = get().songs;
    set({ songs: [...playlistSongs.filter((x) => x !== id)] });
  },
  previousSong: () => {
    const playlist = get();
    const nowPlayingIndex = playlist.songs.findIndex(
      (id) => id === playlist.nowPlayingId
    );
    let prevPlayingIndex = nowPlayingIndex - 1;
    if (prevPlayingIndex === -1) {
      prevPlayingIndex += playlist.songs.length;
    }
    const nowPlaying = playlist.songs[prevPlayingIndex];
    set({ nowPlayingId: nowPlaying });
    return nowPlaying;
  },
  nextSong: () => {
    const playlist = get();
    const nowPlayingIndex = playlist.songs.findIndex(
      (id) => id === playlist.nowPlayingId
    );
    let nextPlayingIndex = (nowPlayingIndex + 1) % playlist.songs.length;
    const nowPlaying = playlist.songs[nextPlayingIndex];
    set({ nowPlayingId: nowPlaying });
    return nowPlaying;
  },
  advance: async () => {
    const { songs, nowPlayingId, loadMore } = get();
    if (songs.length === 0) return "";

    const idx = songs.findIndex((id) => id === nowPlayingId);

    // Still more in the queue: play the next one.
    if (idx !== -1 && idx < songs.length - 1) {
      const next = songs[idx + 1];
      set({ nowPlayingId: next });
      return next;
    }

    // End of the queue.
    // 1) Loop/repeat (account setting) takes precedence: repeat the songs the
    //    user queued rather than pulling in more. This is what makes the Loop
    //    toggle meaningful even when "Play All" enabled continuous autoplay.
    if (usePlayer.getState().loop) {
      const first = songs[0];
      set({ nowPlayingId: first });
      return first;
    }

    // 2) Continuous autoplay (when enabled): pull the next page from the source
    //    (e.g. the feed) and keep going, appending only that page so the queue
    //    stays lean.
    if (usePlayer.getState().autoplayEnabled() && loadMore) {
      const newIds = await loadMore();
      const fresh = (newIds || []).filter((id) => !songs.includes(id));
      if (fresh.length > 0) {
        set({ songs: [...songs, ...fresh], nowPlayingId: fresh[0] });
        return fresh[0];
      }
    }

    // 3) Nothing left to play.
    return "";
  },
}));
